import { actionChannel, call, put, select, take } from 'redux-saga/effects';

import type { SagaIterator } from '@redux-saga/types';

import { ADD_BIG_STAKE, AddBigStake } from '../actions/bigStakes';
import {
  REMOVE_CONFLICTED_ENTRIES, RemoveConflictedEntries,
  RESOLVE_BY_ACCEPT_AVAILABILITY,
  RESOLVE_BY_CANCEL_INSERT,
  RESOLVE_BY_REPLACE_ENTRIES,
  ResolveActions,
  ResolveByReplaceEntries,
  SETUP_CONFLICT,
} from '../actions/conflict';
import {
  cancelInsert,
  INSERT_IN_QUEUE_FOR_ADD, INSERT_IN_QUEUE_FOR_DELETE, InsertInQueueForAdd,
  InsertInQueueForDelete,
  insertInQueueForDelete,
  REPLACE_ENTRIES, ReplaceEntries,
  replaceEntries,
} from '../actions/entries';
import { selectConflictState } from '../selectors/state';
import { ConflictState } from '../types/conflict';
import { isReplaceAction } from '../utils/conflict';
import addEntryFlow from './addEntryFlow';
import conflictResolutionPrompt from './conflictResolutionPrompt';
import removeEntriesFlow from './removeEntriesFlow';
import replaceEntriesFlow from './replaceEntriesFlow';

// ---

type SagaActions =
  | InsertInQueueForAdd
  | InsertInQueueForDelete
  | ReplaceEntries
  | ResolveActions
  | RemoveConflictedEntries
  | AddBigStake;

// Bet slip entries dispatcher saga.
export default function* dispatcher(): SagaIterator {
  const channel = yield actionChannel([
    INSERT_IN_QUEUE_FOR_ADD, INSERT_IN_QUEUE_FOR_DELETE, REPLACE_ENTRIES, SETUP_CONFLICT,
    RESOLVE_BY_CANCEL_INSERT,
    RESOLVE_BY_REPLACE_ENTRIES,
    RESOLVE_BY_ACCEPT_AVAILABILITY,
    REMOVE_CONFLICTED_ENTRIES,
    ADD_BIG_STAKE,
  ]);

  while (true) {
    const action: SagaActions = yield take(channel);
    const conflict: ConflictState = yield select(selectConflictState);

    switch (action.type) {
      case INSERT_IN_QUEUE_FOR_ADD:
        yield call(addEntryFlow, action.payload);
        break;

      case INSERT_IN_QUEUE_FOR_DELETE:
        yield call(removeEntriesFlow, action.payload);
        break;

      case REPLACE_ENTRIES:
        yield call(replaceEntriesFlow, action.payload);
        break;

      case RESOLVE_BY_CANCEL_INSERT:
      case RESOLVE_BY_REPLACE_ENTRIES:
      case RESOLVE_BY_ACCEPT_AVAILABILITY:
        yield call(conflictResolutionPrompt, action);
        break;

      case REMOVE_CONFLICTED_ENTRIES: {
        if (!conflict) {
          console.error('dispatcher -> case REMOVE_CONFLICTED_ENTRIES -> there is no conflict entry');
          break;
        }

        const highlightEntryIdsSize = conflict.highlightEntryIds?.size || 0;
        const removedEntriesLength = action.payload.entryIds.length;
        const totalNumberOfBets = highlightEntryIdsSize - removedEntriesLength;

        if (totalNumberOfBets === 0) {
          const replaceAction = conflict.actions.find((action) => (
            isReplaceAction(action)
          )) as (ResolveByReplaceEntries | undefined);

          if (replaceAction) {
            yield put(cancelInsert(replaceAction.payload.by.bet.entity.id));

            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            yield put(replaceEntries(conflict.highlightEntryIds, replaceAction.payload.by));
          }

          break;
        }
        else {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          yield put(insertInQueueForDelete(action.payload.entryIds));
        }
        break;
      }
    }
  }
}
